import React, { useState } from 'react';
import { UserIcon } from '@common_components/icons/user.icon';
import CellCard from 'components/wrapperEventManager/engagementCell.component';
import UserCard from './userCard.component';
import { EventCommitment } from 'interfaces/types.interface';
import { User, UserWithAvatar } from 'interfaces/user.interface';
import { dayjs, FORMAT_DATE_API, UUID } from '@umanforge/common';
import { useTranslation } from 'react-i18next';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { EventManager } from 'interfaces/eventManager.interface';
import { StyleEventCard } from 'enum/styleEventCard.enum';

interface EventManagerWeeksProps {
  weeks: dayjs.Dayjs[];
  isViewMode?: boolean;
  dataEventManager: Map<string, Map<string, EventCommitment[]>>;
  getParticipantById: ( id: string ) => ( UserWithAvatar & { eventManagersId: UUID[]; } );
  getNextDayNotClosed: ( startDate: dayjs.Dayjs, userId: UUID ) => dayjs.Dayjs;
  isDateInMap: ( dateToCheck: dayjs.Dayjs, userId: UUID ) => boolean;
  setEventClick: React.Dispatch<React.SetStateAction<EventCommitment | undefined>>;
  setEventClicked: React.Dispatch<React.SetStateAction<boolean>>;
  setIsDragging: React.Dispatch<React.SetStateAction<boolean>>;
  isArchived: boolean;
  isDragging: boolean;
  eventManager: EventManager;
  themeHightlighted?: string;
  isPopupCreateEventVisible: boolean;
  setIsPopupCreateEventVisible: React.Dispatch<React.SetStateAction<boolean>>;
  refreshEngagementsOfEventManagerForDays: () => void;
  isAdminOfEventManager: boolean;
  setDateClicked?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setSelectedUserId?: React.Dispatch<React.SetStateAction<string | undefined>>;
  style?: StyleEventCard;
}

const EventManagerWeeks: React.FC<EventManagerWeeksProps> = ( {
  weeks,
  dataEventManager,
  isViewMode,
  getNextDayNotClosed,
  getParticipantById,
  isDateInMap,
  setEventClick,
  setEventClicked,
  setIsDragging,
  isArchived,
  isDragging,
  eventManager,
  themeHightlighted,
  refreshEngagementsOfEventManagerForDays,
  setIsPopupCreateEventVisible,
  isPopupCreateEventVisible,
  setDateClicked,
  setSelectedUserId,
  isAdminOfEventManager,
  style = StyleEventCard.square
} ) => {
  const { t } = useTranslation();
  dayjs.extend( weekOfYear );

  const [ draggedEngagement, setDraggedEngagement ] = useState<EventCommitment | null>( null );
  const [ draggedParticipantStart, setDraggedParticipantStart ] = useState<User>( { id: '' } );

  const [ userHoverDrag, setUserHoverDrag ] = useState<User>( { id: '1', } );
  const [ dayHoverDragInternal, setDayHoverDragInternal ] = useState<dayjs.Dayjs>( dayjs() );
  const setDayHoverDrag = ( d: Date ) => { setDayHoverDragInternal( dayjs( d ) ); };

  const onClickEngagement = ( event: EventCommitment ) => {
    setEventClicked( true );
    setEventClick( event );
  };

  return (
    <div
      className='wrapper mb-32 overflow-visible'
      style={{
        display: 'grid',
        gridTemplateColumns: `225px repeat(${weeks.length}, auto)`,
        gridAutoRows: 'auto',
      }}
    >
      <div></div>
      {/* Displaying weeks */}
      {
        weeks.map( ( week, index ) => {
          return (
            <div key={index} className='flex h-14 items-center justify-center'>
              <div className='text-gray-600 bg-white border-b-2 border-b-orange-400 dark:bg-dark-bg-light dark:text-gray-400 shadow-md w-36 text-center py-1 rounded-t-md font-title'>
                { t( 'common:week' ) + ' ' + week.week() }
              </div>
            </div>
          );
        } )
      }

      {
        dataEventManager != null ?
          <>
            {
              Array.from( dataEventManager.entries() ).map( ( [ participantId, eventMap ] ) => {
                const events: Map<string, EventCommitment[]> = new Map();
                eventMap.forEach( ( value, key ) => {
                  const findedWeek = weeks.find( week => { return week.isSame( dayjs( key ), 'week' ); } );
                  if ( findedWeek ) {
                    const newEventsCommitment = [ ...( events.get( findedWeek.format( FORMAT_DATE_API ) ) ?? [] ), ...value ];
                    events.set( findedWeek.format( FORMAT_DATE_API ), newEventsCommitment );
                  }
                } );
                return (
                  <React.Fragment key={participantId}>
                    {/* Displaying Users */}
                    <UserCard person={ getParticipantById( participantId || '' ) } />

                    {/* Displaying engagements */}
                    {
                      weeks.map( week => {
                        const engagementsForDay = ( events.get( week.format( FORMAT_DATE_API ) ) || [] )
                          .filter( engagement => { return engagement.archived === isArchived; } );

                        return (
                          <CellCard
                            key={`${participantId}-${week.unix()}`}
                            isViewArchived={isArchived}
                            isViewMode={isViewMode}
                            cellClosed={isDateInMap( week, participantId )}
                            themeHightlighted={themeHightlighted}
                            isDragging={isDragging}
                            setIsDragging={setIsDragging}
                            setDraggedParticipantStart={setDraggedParticipantStart}
                            draggedParticipantStart={draggedParticipantStart}
                            draggedEngagement={draggedEngagement}
                            setDraggedEngagement={setDraggedEngagement}
                            dayOver={dayHoverDragInternal.toDate()}
                            userOver={userHoverDrag}
                            setDayOver={setDayHoverDrag}
                            setUserOver={setUserHoverDrag}
                            eventManager={eventManager}
                            dataEventManager={dataEventManager}
                            fetchEngagementsOfEventManagerForDays={refreshEngagementsOfEventManagerForDays}
                            dayCell={week.toDate()}
                            userCell={getParticipantById( participantId )}
                            engagementsCell={engagementsForDay}
                            onEngagementClick={ onClickEngagement }
                            setIsPopupCreateEventVisible={setIsPopupCreateEventVisible}
                            isPopupCreateEventVisible={isPopupCreateEventVisible}
                            setDateClicked={setDateClicked}
                            setSelectedUserId={ setSelectedUserId }
                            canInteract={isAdminOfEventManager ? true : undefined}
                            getNextDayNotClosed={ getNextDayNotClosed }
                            style={ style }
                          />
                        );
                      } )
                    }
                  </React.Fragment>
                );}
              ) }
          </>
          :
          <React.Fragment key={'person.id'}>
            {
              Array( 3 )
                .fill( null )
                .map( ( _, idx ) => {
                  return (
                    <React.Fragment key={idx}>
                      <div className='flex h-full flex-center flex-col min-h-48 cursor-pointer'>
                        <UserIcon size={8} className='bg-white shadow dark:bg-dark-bg-light rounded-xl h-20 w-20 p-2' />
                        <div className='text-base mt-3 bg-gray-base border px-8 rounded-full py-2' />
                      </div>
                      {
                        weeks.map( ( day, index ) => {
                          return (
                            <div
                              key={index}
                              className='trans-fast m-[3px] h-48 bg-[#ffffff] border-white dark:bg-dark-bg-light2 min-w-48 p-2 border-2 flex gap-2 flex-wrap rounded-xl shadow-md items-center justify-center'
                            />
                          );
                        } )
                      }
                    </React.Fragment>
                  );
                } )
            }
          </React.Fragment>
      }

    </div>
  );
};

export default EventManagerWeeks;
